import React, { ReactNode, useEffect, useRef, useState } from "react";
import OpenAI from "openai";
import { cn } from "@/lib/utils";
import { inject, observer } from "mobx-react";
import { Store } from "stores/store";
import Age from "../../components/pages/chatbot/Age";
import { ChatbotUiState, UiState } from "@/components/enums/UiState";
import Hint from "../../components/pages/chatbot/Hint";
import Tutorial from "../../components/pages/chatbot/Tutorial";
import Landing from "@/components/pages/chatbot/Landing";
import {
    AgeEntity,
    CancelCallEntity,
    ChatbotEntity,
    ChatSteleEntity,
    EndOfCallOldEntity,
    EndOfCallRegularEntity,
    GlobalEntity,
    GlobalEventEntity,
    HintEntity,
    I18NEntity,
    ImprintEntity,
    LandingEntity,
    LoadingEntity,
    MaskEntity,
    MetaEntity,
    PostProcessingEntity,
    PrivacyPolicyEntity,
    TutorialEntity,
} from "types/strapi";
import { ChatAiBranchEntity, ChatBranchEntity, ChatBranchOver50Entity, ChatEventBranchEntity } from "types/chat";
import axios from "axios";
import { fetchFromStrapi } from "utils/graphqlClient";
import {
    ageQuery,
    allI18nsQuery,
    cancelCallQuery,
    chatAiBranchesQuery,
    chatbotQuery,
    chatBranchesQuery,
    chatBranchOver50SQuery,
    chatEventBranchesQuery,
    chatSteleQuery,
    endOfCallOldQuery,
    endOfCallRegularQuery,
    globalEventQuery,
    globalQuery,
    hintQuery,
    imprintQuery,
    landingQuery,
    loadingQuery,
    maskQuery,
    metaQuery,
    postProcessings,
    privacyPolicyQuery,
    tutorialQuery,
} from "utils/graphql/queries";
import WebcamCapture from "@/components/shared/webcam/WebcamCapture";
import Mask from "@/components/pages/chatbot/Mask";
import Loading from "@/components/pages/chatbot/Loading";
import PrivacyPolicy from "@/components/pages/chatbot/PrivacyPolicy";
import Imprint from "@/components/pages/chatbot/Imprint";
import { Tracking } from "@/components/shared/tracking/Tracking";
import Chat from "@/components/pages/chatbot/Chat";
import { ImageProcessing } from "@/components/shared/imageProcessing/ImageProcessing";
import { isDev } from "@/constant/env";
import DebugControls from "@/components/shared/debug";
import LandscapeWarning from "@/components/chat/LandscapeWarning";
import useUserDevice from "@/components/shared/webcam/hooks/useUserDevice";
import Head from "next/head";
import Selfie from "@/components/pages/chatbot/Selfie";
import { NetworkGuard } from "@/components/shared/NetworkGuard/NetworkGuard";
import DesktopFallback from "@/components/pages/chatbot/DesktopFallback";

interface ExtendedChatCompletionMessageParam {
    id?: string;
    role: string;
    content: string;
}

function Chatbot(props: {
    store?: Store;
    i18nData: I18NEntity[];
    locales: string[];
    localeNames: string[];
    metaData: MetaEntity;
    postProcessings: PostProcessingEntity[];
    landingData: LandingEntity;
    ageData: AgeEntity;
    cancelCallData: CancelCallEntity;
    endOfCallOldData: EndOfCallOldEntity;
    endOfCallRegularData: EndOfCallRegularEntity;
    hintData: HintEntity;
    loadingData: LoadingEntity;
    maskData: MaskEntity;
    tutorialData: TutorialEntity;
    globalData: GlobalEntity;
    chatSteleData: ChatSteleEntity;
    chatBranchesData: ChatBranchEntity[];
    chatEventBranchesData: ChatEventBranchEntity[];
    chatAiBranchesData: ChatAiBranchEntity[];
    chatBranchOver50Data: ChatBranchOver50Entity[];
    privacyPolicyData: PrivacyPolicyEntity;
    imprintData: ImprintEntity;
    chatbotData: ChatbotEntity;
    globalEventData: GlobalEventEntity;
}) {
    if (!props.store) {
        throw Error("Component was not setup correctly");
    }

    const {
        updateCaptureFace,
        updateRenderContent,
        landingLocalized,
        updateLandingLocalized,
        ageLocalized,
        updateAgeLocalized,
        cancelCallLocalized,
        updateCancelCallLocalized,
        endOfCallOldLocalized,
        updateEndOfCallOldLocalized,
        endOfCallRegularLocalized,
        updateEndOfCallRegularLocalized,
        hintLocalized,
        updateHintLocalized,
        loadingLocalized,
        updateLoadingLocalized,
        maskLocalized,
        updateMaskLocalized,
        tutorialLocalized,
        updateTutorialLocalized,
        globalLocalized,
        updateGlobalLocalized,
        privacyPolicyLocalized,
        updatePrivacyPolicyLocalized,
        imprintLocalized,
        updateImprintLocalized,
        chatSteleLocalized,
        updateChatSteleLocalized,
        updateChatBranches,
        updateChatBranchesOver50,
        updateChatEventBranches,
        locale,
        locales,
        updateLocale,
        updateLocales,
        updateLocaleNames,
        updateI18nData,
        resetAll,
        chatbotLocalized,
        updateChatbotLocalized,
        updateRenderChatbotContent,
        renderChatbotContent,
        updateChatAiBranches,
        updateFaceNotDetected,
        updateChatProjectionSceneReady,
        updateIsPortrait,
        metaLocalized,
        isPortrait,
        updateMetaLocalized,
        globalEventLocalized,
        updateGlobalEventLocalized
    } = props.store;

    const { device } = useUserDevice();

    useEffect(() => {
        if ("orientation" in screen && (screen.orientation as any).lock) {
            (screen.orientation as any).lock("portrait").catch((error: any) => {
                console.error("Orientation lock failed:", error);
            });
        }

        updateLocales(props.locales);
        updateLocaleNames(props.localeNames);
        updateChatBranches(props.chatBranchesData);
        updateChatEventBranches(props.chatEventBranchesData);
        updateChatAiBranches(props.chatAiBranchesData);
        updateChatBranchesOver50(props.chatBranchOver50Data);
        updateI18nData(props.i18nData);
    }, []);

    useEffect(() => {
        if (metaLocalized?.locale !== locale && locale !== undefined) {
            if (props.metaData.attributes?.locale === locale) {
                updateMetaLocalized(props.metaData.attributes);
            } else {
                if (props.metaData.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.metaData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.metaData.attributes.localizations.data[localizationsIndex];

                        if (localization.attributes?.locale === locale) {
                            updateMetaLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        const browserLanguage = navigator.language;

        let languageInitialized = false;
        for (let localeIndex = 0; localeIndex < locales.length; localeIndex++) {
            const locale = locales[localeIndex];

            if (browserLanguage.includes(locale)) {
                languageInitialized = true;
                updateLocale(locale, true);
            }
        }

        if (!languageInitialized) {
            updateLocale(locales[0], true);
        }
    }, [locales]);

    useEffect(() => {
        if (landingLocalized?.locale !== locale && locale !== undefined) {
            if (props.landingData?.attributes?.locale === locale) {
                updateLandingLocalized(props.landingData.attributes);
            } else {
                if (props.landingData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.landingData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.landingData.attributes.localizations.data[localizationsIndex];

                        if (localization.attributes?.locale === locale) {
                            updateLandingLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (ageLocalized?.locale !== locale && locale !== undefined) {
            if (props.ageData?.attributes?.locale === locale) {
                updateAgeLocalized(props.ageData.attributes);
            } else {
                if (props.ageData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.ageData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.ageData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateAgeLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (cancelCallLocalized?.locale !== locale && locale !== undefined) {
            if (props.cancelCallData?.attributes?.locale === locale) {
                updateCancelCallLocalized(props.cancelCallData.attributes);
            } else {
                if (props.cancelCallData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.cancelCallData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.cancelCallData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateCancelCallLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (endOfCallOldLocalized?.locale !== locale && locale !== undefined) {
            if (props.endOfCallOldData?.attributes?.locale === locale) {
                updateEndOfCallOldLocalized(props.endOfCallOldData.attributes);
            } else {
                if (props.endOfCallOldData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.endOfCallOldData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.endOfCallOldData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateEndOfCallOldLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (endOfCallRegularLocalized?.locale !== locale && locale !== undefined) {
            if (props.endOfCallRegularData?.attributes?.locale === locale) {
                updateEndOfCallRegularLocalized(props.endOfCallRegularData.attributes);
            } else {
                if (props.endOfCallRegularData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.endOfCallRegularData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization =
                            props.endOfCallRegularData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateEndOfCallRegularLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (hintLocalized?.locale !== locale && locale !== undefined) {
            if (props.hintData?.attributes?.locale === locale) {
                updateHintLocalized(props.hintData.attributes);
            } else {
                if (props.hintData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.hintData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.hintData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateHintLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (loadingLocalized?.locale !== locale && locale !== undefined) {
            if (props.loadingData?.attributes?.locale === locale) {
                updateLoadingLocalized(props.loadingData.attributes);
            } else {
                if (props.loadingData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.loadingData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.loadingData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateLoadingLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (globalLocalized?.locale !== locale && locale !== undefined) {
            if (props.globalData?.attributes?.locale === locale) {
                updateGlobalLocalized(props.globalData.attributes);
            } else {
                if (props.globalData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.globalData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.globalData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateGlobalLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (maskLocalized?.locale !== locale && locale !== undefined) {
            if (props.maskData?.attributes?.locale === locale) {
                updateMaskLocalized(props.maskData.attributes);
            } else {
                if (props.maskData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.maskData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.maskData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateMaskLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (tutorialLocalized?.locale !== locale && locale !== undefined) {
            if (props.tutorialData?.attributes?.locale === locale) {
                updateTutorialLocalized(props.tutorialData.attributes);
            } else {
                if (props.tutorialData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.tutorialData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.tutorialData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateTutorialLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (chatSteleLocalized?.locale !== locale && locale !== undefined) {
            if (props.chatSteleData?.attributes?.locale === locale) {
                updateChatSteleLocalized(props.chatSteleData.attributes);
            } else {
                if (props.chatSteleData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.chatSteleData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.chatSteleData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateChatSteleLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (privacyPolicyLocalized?.locale !== locale && locale !== undefined) {
            if (props.privacyPolicyData?.attributes?.locale === locale) {
                updatePrivacyPolicyLocalized(props.privacyPolicyData.attributes);
            } else {
                if (props.privacyPolicyData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.privacyPolicyData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.privacyPolicyData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updatePrivacyPolicyLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (imprintLocalized?.locale !== locale && locale !== undefined) {
            if (props.imprintData?.attributes?.locale === locale) {
                updateImprintLocalized(props.imprintData.attributes);
            } else {
                if (props.imprintData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.imprintData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.imprintData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateImprintLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (chatbotLocalized?.locale !== locale && locale !== undefined) {
            if (props.chatbotData?.attributes?.locale === locale) {
                updateChatbotLocalized(props.chatbotData.attributes);
            } else {
                if (props.chatbotData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.chatbotData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.chatbotData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateChatbotLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        if (globalEventLocalized?.locale !== locale && locale !== undefined) {
            if (props.globalEventData?.attributes?.locale === locale) {
                updateGlobalEventLocalized(props.globalEventData.attributes);
            } else {
                if (props.globalEventData?.attributes?.localizations) {
                    for (
                        let localizationsIndex = 0;
                        localizationsIndex < props.globalEventData.attributes.localizations.data.length;
                        localizationsIndex++
                    ) {
                        const localization = props.globalEventData.attributes.localizations.data[localizationsIndex];

                        if (localization?.attributes?.locale === locale) {
                            updateGlobalEventLocalized(localization.attributes);
                            break;
                        }
                    }
                }
            }
        }
    }, [locale]);

    useEffect(() => {
        setMessages([
            {
                role: "system",
                content: chatbotLocalized?.chatbotPrompt ?? "",
            },
            {
                role: "assistant",
                content:
                    "Verbindung in die Zukunft wird hergestellt … Link stabil … Verbindung hergestellt. Äh, hallo?! Ist da wirklich mein Gegenwarts-Ich?",
            },
        ]);
    }, []);

    const [userText, setUserText] = useState("");
    const [messages, setMessages] = useState<ExtendedChatCompletionMessageParam[]>([]);
    const [questionCounter, setQuestionCounter] = useState(1);
    const [waiting, setWaiting] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const chatModel = "gpt-4o";
    const chatTemperature = 1;
    const chatMaxTokens = 256;
    const questionLimit: number = 10;

    // const openai = new OpenAI({
    //     apiKey: process.env.NEXT_PUBLIC_CHAT_GPT_KEY,
    //     dangerouslyAllowBrowser: true,
    // });

    const streamMessageId = `stream-${Date.now()}`;

    const handleSetMessages = (newMessage: string) => {
        setMessages((prevMessages) => [...prevMessages, { role: "user", content: newMessage }]);
    };

    const sendUserQuestion = (userInput: string) => {
        setUserText("");
        if (inputRef.current) {
            inputRef.current.blur();
            inputRef.current.style.height = "40px";
        }
        const newMessages = [...messages, { role: "user", content: userInput }];
        handleSetMessages(userInput);
        console.log(messages);
        setQuestionCounter((prevCount) => prevCount + 1);
        if (inputRef.current) {
            inputRef.current.style.height = "auto";
        }
        sendMessage(newMessages);
    };

    const sendMessage = async (updatedMessages: any) => {
        // setWaiting(true);
        // console.log("COUNTER: " + questionCounter);
        // console.log("LIMIT: " + questionLimit);

        // if (questionCounter === questionLimit) {
        //     setMessages((prevMessages) => [
        //         ...prevMessages,
        //         {
        //             role: "assistant",
        //             content:
        //                 "Oh entschuldige, ich muss hier leider weiter. Die Freunde drängeln beim Holo-Golf. Lass doch mal wieder quatschen! Nummer hast du ja jetzt. Hat mich echt gefreut dich, äh, mich wiederzusehen! Achja, den Kontakt bin ich dir noch schuldig: [Link: Sparkassen-Berater]! Grüß schön! ",
        //         },
        //     ]);
        // } else {
        //     const completion = await openai.chat.completions.create({
        //         model: chatModel,
        //         messages: updatedMessages,
        //         temperature: chatTemperature,
        //         max_tokens: chatMaxTokens,
        //         top_p: 1,
        //         frequency_penalty: 0,
        //         presence_penalty: 0,
        //         stream: true,
        //     });

        //     let updatedStreamMessage = "";
        //     for await (const chunk of completion) {
        //         if (!chunk.choices[0].delta.content) continue;
        //         updatedStreamMessage += chunk.choices[0].delta.content;
        //         setMessages((prevMessages) => [
        //             ...prevMessages.filter((msg) => msg.id !== streamMessageId),
        //             { id: streamMessageId, role: "assistant", content: updatedStreamMessage },
        //         ]);
        //     }
        //     setMessages([
        //         ...updatedMessages,
        //         { id: streamMessageId, role: "assistant", content: updatedStreamMessage },
        //     ]);
        //     setWaiting(false);
        // }
    };

    const endOfMessagesRef = useRef<HTMLDivElement>(null);

    const [over50, setOver50] = useState<boolean>(false);

    const handleOver50 = (age: boolean) => {
        setOver50(age);
    };

    const cameraBg: boolean =
        (renderChatbotContent === ChatbotUiState.CHATBOT && over50) ||
        renderChatbotContent === ChatbotUiState.HINT ||
        renderChatbotContent === ChatbotUiState.AGE ||
        renderChatbotContent === ChatbotUiState.MASK ||
        renderChatbotContent === ChatbotUiState.OVER50 ||
        renderChatbotContent === ChatbotUiState.LOADING;

    useEffect(() => {
        endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const changeUiState = (state: ChatbotUiState): void => {
        updateRenderChatbotContent(state);
    };

    const [selfieOrCall, setSelfieOrCall] = useState<"selfie" | "call">("call")

    const renderUiState = (state: ChatbotUiState): ReactNode => {
        switch (state) {
            case ChatbotUiState.LANDING:
                return (
                    <Landing
                        onLetsGo={() => updateRenderChatbotContent(ChatbotUiState.AGE)}
                        onHowItWorks={() => updateRenderChatbotContent(ChatbotUiState.TUTORIAL)}
                        onTakeSelfie={() => updateRenderChatbotContent(ChatbotUiState.MASK)}
                        setSelfieOrCall={setSelfieOrCall}
                    />
                );
            case ChatbotUiState.AGE:
                return (
                    <Age
                        handleOver50={handleOver50}
                        onCancel={() => updateRenderChatbotContent(ChatbotUiState.LANDING)}
                        onLetsGo={() => updateRenderChatbotContent(ChatbotUiState.MASK)}
                        onLetsGo50={() => updateRenderChatbotContent(ChatbotUiState.LOADING)}
                    />
                );
            case ChatbotUiState.MASK:
                return (
                    <Mask
                        onCall={() => {
                            console.log("Setting capture face to true");
                            updateCaptureFace(true);
                            updateRenderChatbotContent(ChatbotUiState.LOADING);
                            updateFaceNotDetected(false);
                        }}
                        onGoBack={() => {
                            updateRenderChatbotContent(ChatbotUiState.LANDING);
                            updateFaceNotDetected(false);
                        }}
                        selfieOrCall={selfieOrCall}
                    />
                );
            case ChatbotUiState.LOADING:
                return (
                    <Loading
                        onReady={() => updateRenderChatbotContent(ChatbotUiState.CHATBOT)}
                        onSelfie={() => updateRenderChatbotContent(ChatbotUiState.SELFIE)}
                        over50={over50}
                        selfieOrCall={selfieOrCall}
                        onReadyOver50={() => {
                            updateRenderChatbotContent(ChatbotUiState.OVER50);
                            updateChatProjectionSceneReady(true);
                        }}
                        onGoBack={() => {
                            updateRenderChatbotContent(ChatbotUiState.LANDING);
                            resetAll();
                        }}
                    />
                );
            case ChatbotUiState.SELFIE:
                return <Selfie
                    onCancel={() => {
                        updateRenderChatbotContent(ChatbotUiState.LANDING)
                        resetAll();
                    }}
                    onNewSelfie={() => {
                        updateRenderChatbotContent(ChatbotUiState.MASK)
                        resetAll();
                    }}
                />;
            case ChatbotUiState.POLICY:
                return <PrivacyPolicy onClose={() => updateRenderChatbotContent(ChatbotUiState.LANDING)} />;
            case ChatbotUiState.IMPRINT:
                return <Imprint onClose={() => updateRenderChatbotContent(ChatbotUiState.LANDING)} />;
            case ChatbotUiState.HINT:
                return (
                    <Hint
                        onCall={() => updateRenderChatbotContent(ChatbotUiState.CHATBOT)}
                        onCancel={() => (window.location.href = "/")}
                    />
                );
            case ChatbotUiState.TUTORIAL:
                return (
                    <Tutorial
                        onCall={() => updateRenderChatbotContent(ChatbotUiState.AGE)}
                        onCancel={() => updateRenderChatbotContent(ChatbotUiState.LANDING)}
                    />
                );
            case ChatbotUiState.CHATBOT:
                /* TODO: why is this commented?
                if(imageAgedUrl === undefined && !over50) {
                    return <Loading
                            onReady={() => updateRenderChatbotContent(ChatbotUiState.CHATBOT)}
                            over50={over50}
                            onReadyOver50={() => updateRenderChatbotContent(ChatbotUiState.OVER50)}
                            onGoBack={() => {
                                updateRenderChatbotContent(ChatbotUiState.LANDING), resetAll();
                            }}
                        />
                }*/

                return (
                    <Chat
                        onCancelCall={async () => {
                            try {
                                console.log("onCancelCall");
                                /* TODO: tbc are we using the same logging for online?
                            wait handleLogOnReset(
                                renderContent,
                                actualChatThread[actualChatThread.length - 1],
                                ResetTrigger.CANCELCALLCHAT,
                            );*/
                                resetAll();
                            } catch (error) {
                                console.error("Fehler beim Loggen des Events: ", error);
                                resetAll();
                            }
                        }}
                        onReloadCall={async () => {
                            try {
                                console.log("onReloadCall");
                                /* TODO: tbc are we using the same logging for online?
                            await handleLogOnReset(
                                renderContent,
                                actualChatThread[actualChatThread.length - 1],
                                ResetTrigger.RELOADCALLCHAT,
                            );*/
                                resetAll();
                                updateRenderContent(UiState.AGE);
                                updateRenderChatbotContent(ChatbotUiState.AGE);
                            } catch (error) {
                                console.error("Fehler beim Loggen des Events: ", error);
                                resetAll();
                                updateRenderContent(UiState.AGE);
                                updateRenderChatbotContent(ChatbotUiState.AGE);
                            }
                        }}
                        isByod={true}
                    />
                );
            case ChatbotUiState.OVER50:
                return (
                    <Chat
                        isOver50={true}
                        onCancelCall={async () => {
                            try {
                                console.log("onCancelCall");
                                /* TODO: tbc are we using the same logging for online?
                            wait handleLogOnReset(
                                renderContent,
                                actualChatThread[actualChatThread.length - 1],
                                ResetTrigger.CANCELCALLCHAT,
                            );*/
                                resetAll();
                            } catch (error) {
                                console.error("Fehler beim Loggen des Events: ", error);
                                resetAll();
                            }
                        }}
                        onReloadCall={async () => {
                            try {
                                console.log("onReloadCall");
                                /* TODO: tbc are we using the same logging for online?
                            await handleLogOnReset(
                                renderContent,
                                actualChatThread[actualChatThread.length - 1],
                                ResetTrigger.RELOADCALLCHAT,
                            );*/
                                resetAll();
                                updateRenderContent(UiState.AGE);
                                updateRenderChatbotContent(ChatbotUiState.AGE);
                            } catch (error) {
                                console.error("Fehler beim Loggen des Events: ", error);
                                resetAll();
                                updateRenderContent(UiState.AGE);
                                updateRenderChatbotContent(ChatbotUiState.AGE);
                            }
                        }}
                    />
                );
        }
    };

    const [orientation, setOrientation] = useState<OrientationType>();

    useEffect(() => {
        // Check if the screen object is available
        if (typeof window !== "undefined" && window.screen) {
            setOrientation(window.screen.orientation.type);

            if (screen.orientation.type.startsWith("portrait")) {
                updateIsPortrait(true);
            } else {
                updateIsPortrait(false);
            }

            const handleOrientationChange = () => {
                setOrientation(window.screen.orientation.type);
                if (window.screen.orientation.type.startsWith("portrait")) {
                    updateIsPortrait(true);
                } else {
                    updateIsPortrait(false);
                }
            };

            // Add event listener for orientation changes
            window.screen.orientation.addEventListener("change", handleOrientationChange);

            // Clean up the event listener on component unmount
            return () => {
                window.screen.orientation.removeEventListener("change", handleOrientationChange);
            };
        }
    }, []);

    //TODO: delte/rework because this causes cameras to fail on samsung devices
    // const checkCameraPermissions = async () => {
    //     try {
    //         await navigator.mediaDevices.getUserMedia({ video: true });
    //         setHasPermission(true);
    //     } catch (error) {
    //         setHasPermission(false);
    //     }
    // };

    // useEffect(() => {
    //     checkCameraPermissions();
    // }, []);

    const title = metaLocalized?.ogTitle && metaLocalized?.ogTitle.toString();
    const description = metaLocalized?.ogDescription && metaLocalized?.ogDescription.toString();

    const [windowHeight, setWindowHeight] = useState(0);

    useEffect(() => {
        setWindowHeight(window.innerHeight);

        const handleResize = () => {
            setWindowHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // useEffect(() => {
    //     const handleTouchMove = (e: TouchEvent) => {
    //         e.preventDefault();
    //     };

    //     document.addEventListener("touchmove", handleTouchMove, { passive: false });

    //     return () => {
    //         document.removeEventListener("touchmove", handleTouchMove);
    //     };
    // }, []);

    return (
        <NetworkGuard>
            <Tracking>
                <Head>
                    <title>Triff dein Zukunfts-Ich</title>
                    <meta property="og:title" content={"Triff dein Zukunfts-Ich"} />
                    <meta
                        property="og:description"
                        content={
                            description ??
                            "Triff dein Zukunfts-Ich in diesem Dialog durch die Zeit. Frag dein älteres Ich, wie die Zukunft aussieht, und hol dir gute Tipps fürs Heute, damit es dir in Zukunft gut geht! Starte jetzt deinen Anruf durch die Zeit!"
                        }
                    />
                    <meta property="og:url" content="https://triffdeinezukunft.de/" />
                    <meta property="og:image" content="https://nl-assets.curious.zone/MetaTagImage.jpg" />
                    <meta property="og:type" content="website" />

                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:title" content={"Triff dein Zukunfts-Ich"} />
                    <meta
                        name="twitter:description"
                        content={
                            description ??
                            "Triff dein Zukunfts-Ich in diesem Dialog durch die Zeit. Frag dein älteres Ich, wie die Zukunft aussieht, und hol dir gute Tipps fürs Heute, damit es dir in Zukunft gut geht! Starte jetzt deinen Anruf durch die Zeit!"
                        }
                    />
                    <meta name="twitter:image" content="https://nl-assets.curious.zone/MetaTagImage.jpg" />
                    <meta name="twitter:url" content="https://triffdeinezukunft.de/" />
                </Head>

                <ImageProcessing>
                    <div
                        className="scrollbar-hidden absolute flex justify-center items-center bg-dark-300 w-screen overflow-hidden"
                        style={{ height: windowHeight, maxHeight: windowHeight }}
                    >
                        {renderChatbotContent !== ChatbotUiState.LANDING &&
                            renderChatbotContent !== ChatbotUiState.TUTORIAL && (
                                <WebcamCapture
                                    showMask={renderChatbotContent === ChatbotUiState.MASK}
                                    showBlur={renderChatbotContent === ChatbotUiState.AGE}
                                    showToggleIfAvailable={
                                        renderChatbotContent === ChatbotUiState.AGE ||
                                        renderChatbotContent === ChatbotUiState.MASK
                                    }
                                    className={cn(
                                        "absolute",
                                        cameraBg ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0",
                                    )}
                                />
                            )}
                        {renderUiState(renderChatbotContent)}
                        {/* {isDev ? (
                            <DebugControls
                                uiStateEnum={ChatbotUiState}
                                currentState={renderChatbotContent}
                                onStageChange={changeUiState}
                            />
                        ) : null} */}
                        {/* {device?.isHandheld && !device.isTablet && !isPortrait && <LandscapeWarning />} */}
                        {/* <div className="top-0 left-0 absolute">
                        {orientation}
                    </div> */}
                        {/* {!hasPermission && renderChatbotContent === ChatbotUiState.AGE && (
                        <NoCameraPermission
                            onGrant={() => checkCameraPermissions}
                            onCancel={() => updateRenderChatbotContent(ChatbotUiState.LANDING)}
                        />
                    )} */}
                        {/* <div className="hidden md:block pointer-events-none">
                            <DesktopFallback />
                        </div> */}
                    </div>
                    {/* <div className="hidden md:block pointer-events-none">
                        <DesktopFallback />
                    </div> */}
                </ImageProcessing>
            </Tracking>
        </NetworkGuard>
    );
}

export async function getStaticProps() {
    const localesData = await axios.get<
        {
            id: number;
            name: string;
            code: string;
            createdAt: string;
            updatedAt: string;
            isDefault: boolean;
        }[]
    >(process.env.NEXT_PUBLIC_STRAPI_URL + "/api/i18n/locales");

    const locales = [];
    const localeNames = [];

    for (let localeDataIndex = 0; localeDataIndex < localesData.data.length; localeDataIndex++) {
        const localeData = localesData.data[localeDataIndex];

        locales.push(localeData.code);
        localeNames.push(localeData.name.split(" ")[0]);
    }

    const i18nData = await fetchFromStrapi(allI18nsQuery);
    const metaData = await fetchFromStrapi(metaQuery);
    const postProcessingData = await fetchFromStrapi(postProcessings);
    const landingData = await fetchFromStrapi(landingQuery);
    const ageData = await fetchFromStrapi(ageQuery);
    const cancelCallData = await fetchFromStrapi(cancelCallQuery);
    const endOfCallOldData = await fetchFromStrapi(endOfCallOldQuery);
    const endOfCallRegularData = await fetchFromStrapi(endOfCallRegularQuery);
    const hintData = await fetchFromStrapi(hintQuery);
    const loadingData = await fetchFromStrapi(loadingQuery);
    const maskData = await fetchFromStrapi(maskQuery);
    const tutorialData = await fetchFromStrapi(tutorialQuery);
    const globalData = await fetchFromStrapi(globalQuery);
    const chatSteleData = await fetchFromStrapi(chatSteleQuery);
    const chatBranchesData = await fetchFromStrapi(chatBranchesQuery);
    const chatEventBranchesData = await fetchFromStrapi(chatEventBranchesQuery);
    const chatAiBranchesData = await fetchFromStrapi(chatAiBranchesQuery);
    const chatBranchOver50Data = await fetchFromStrapi(chatBranchOver50SQuery);
    const privacyPolicyData = await fetchFromStrapi(privacyPolicyQuery);
    const imprintData = await fetchFromStrapi(imprintQuery);
    const chatbotData = await fetchFromStrapi(chatbotQuery);
    const globalEventData = await fetchFromStrapi(globalEventQuery);

    return {
        props: {
            i18nData: i18nData.i18Ns.data,
            locales,
            localeNames,
            metaData: metaData.meta.data,
            postProcessings: postProcessingData.postProcessings.data,
            landingData: landingData.landing.data,
            ageData: ageData.age.data,
            cancelCallData: cancelCallData.cancelCall.data,
            endOfCallOldData: endOfCallOldData.endOfCallOld.data,
            endOfCallRegularData: endOfCallRegularData.endOfCallRegular.data,
            hintData: hintData.hint.data,
            loadingData: loadingData.loading.data,
            maskData: maskData.mask.data,
            tutorialData: tutorialData.tutorial.data,
            globalData: globalData.global.data,
            chatSteleData: chatSteleData.chatStele.data,
            chatBranchesData: chatBranchesData?.chatBranches?.data ?? null,
            chatEventBranchesData: chatEventBranchesData?.chatEvents?.data ?? null,
            chatBranchOver50Data: chatBranchOver50Data?.chatBranchOver50S?.data ?? null,
            chatAiBranchesData: chatAiBranchesData?.chatBranchAis?.data ?? null,
            privacyPolicyData: privacyPolicyData.privacyPolicy.data,
            imprintData: imprintData.imprint.data,
            chatbotData: chatbotData.chatbot.data,
            globalEventData: globalEventData.globalEvent.data,
        },
    };
}

export default inject("store")(observer(Chatbot));
